import React from "react";
import PropTypes from "prop-types";
import { Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import { STATIC } from "../../utils/constants/appConstants";

/**
 * CommonModal
 * Reusable modal wrapper for consistent styling and logic.
 *
 * Optional style class props:
 * - modalClassName
 * - headerClassName
 * - bodyClassName
 * - footerClassName
 */
const CommonModal = ({
  isOpen,
  toggle,
  title,
  children,
  size = "md",
  modalClassName = "",
  headerClassName = "",
  bodyClassName = "",
  footerClassName = "",
  footerButtons = [],
  hideFooter = false,
  backDrop = false,
  scrollable = true,
  fullscreen = false
}) => {
  const hideCloseBtn = headerClassName?.includes("hide-close-btn");

  return (
    <Modal
      isOpen={isOpen}
      toggle={toggle}
      size={size}
      scrollable={scrollable}
      fullscreen={fullscreen}
      className={modalClassName.trim()}
      backdrop={backDrop ? STATIC : true}
      keyboard={!backDrop}
      backdropClassName={backDrop ? "bg-dark bg-opacity-1" : ""}
    >
      {title && (
        <ModalHeader
          toggle={hideCloseBtn ? undefined : toggle}
          className={`${headerClassName || ""} position-relative`}
        >
          {title}
        </ModalHeader>
      )}

      <ModalBody className={bodyClassName || ""}>{children}</ModalBody>

      {!hideFooter && footerButtons?.length > 0 && (
        <ModalFooter className={`${footerClassName || ""} position-relative`}>
          {footerButtons.map((btn, idx) => (
            <button
              key={idx}
              type="button"
              onClick={btn.onClick}
              className={btn.className || "btn btn-primary"}
              disabled={btn.disabled || false}
            >
              {btn.label}
            </button>
          ))}
        </ModalFooter>
      )}
    </Modal>
  );
};

CommonModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  toggle: PropTypes.func.isRequired,
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  children: PropTypes.node.isRequired,
  size: PropTypes.string,
  modalClassName: PropTypes.string,
  headerClassName: PropTypes.string,
  bodyClassName: PropTypes.string,
  footerClassName: PropTypes.string,
  footerButtons: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      color: PropTypes.string,
      onClick: PropTypes.func,
      disabled: PropTypes.bool
    })
  ),
  hideFooter: PropTypes.bool,
  backDrop: PropTypes.bool,
  scrollable: PropTypes.bool,
  fullscreen: PropTypes.oneOfType([PropTypes.bool, PropTypes.string])
};

export default CommonModal;
